
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";

const PROGRAMS_PER_PAGE = 18; // Reduced from 20 to 18 to avoid footer overlap

interface ProgramData {
  title: string;
  category: string;
  student: string;
  enrolledAt: string;
  completionDate: string;
  remainingDays: number | null;
  progress: number;
  status: string;
}

/**
 * Export programs list as JPG with pagination support
 */
export const printProgramsListPaginatedAsJPG = async (
  programs: ProgramData[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log('Starting programs list JPG download...', { programs: programs.length, startPage, endPage });
    
    const loadingToast = toast.loading('Generating programs list JPG...');
    
    const { content, filename } = generateProgramListContent(programs, startPage, endPage);
    
    await downloadAsJPG(content, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download programs list');
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`Programs list ${pageText} downloaded as JPG!`);
    
  } catch (error) {
    console.error('Error downloading paginated programs list:', error);
    toast.error('Failed to download programs list');
  }
};

/**
 * Export programs list as PDF with pagination support
 */
export const printProgramsListPaginatedAsPDF = async (
  programs: ProgramData[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log('Starting programs list PDF download...', { programs: programs.length, startPage, endPage });
    
    const loadingToast = toast.loading('Generating programs list PDF...');
    
    const { content, filename } = generateProgramListContent(programs, startPage, endPage);
    
    await downloadAsPDF(content, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download programs list');
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`Programs list ${pageText} downloaded as PDF!`);
    
  } catch (error) {
    console.error('Error downloading paginated programs list as PDF:', error);
    toast.error('Failed to download programs list');
  }
};

/**
 * Generate common content for both JPG and PDF downloads
 */
const generateProgramListContent = (
  programs: ProgramData[],
  startPage: number,
  endPage: number
) => {
  const totalPages = Math.ceil(programs.length / PROGRAMS_PER_PAGE);
  const actualEndPage = Math.min(endPage, totalPages);
  const actualStartPage = Math.max(1, startPage);
  
  // Calculate which programs to include
  const startIndex = (actualStartPage - 1) * PROGRAMS_PER_PAGE;
  const endIndex = actualEndPage * PROGRAMS_PER_PAGE;
  const programsToShow = programs.slice(startIndex, endIndex);
  
  // Group programs by pages
  const pages: ProgramData[][] = [];
  for (let i = 0; i < programsToShow.length; i += PROGRAMS_PER_PAGE) {
    pages.push(programsToShow.slice(i, i + PROGRAMS_PER_PAGE));
  }
  
  // Generate content for all pages
  const allPagesContent = pages.map((pagePrograms, pageIndex) => {
    const currentPageNumber = actualStartPage + pageIndex;
    
    return `
      <div class="page-content" style="page-break-after: ${pageIndex < pages.length - 1 ? 'always' : 'auto'}; min-height: 1050px; display: flex; flex-direction: column;">
        ${getHeaderHtml(`Programs List - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="page-info">
          <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
          Programs ${(currentPageNumber - 1) * PROGRAMS_PER_PAGE + 1} - ${Math.min(currentPageNumber * PROGRAMS_PER_PAGE, programs.length)} of ${programs.length}
        </div>
        
        <div class="table-container" style="flex: 1; margin-bottom: 20px;">
          <table>
            <thead>
              <tr>
                <th>Program Title</th>
                <th>Category</th>
                <th>Student</th>
                <th>Enrolled</th>
                <th>Completion Date</th>
                <th>Remaining</th>
                <th>Progress</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${pagePrograms.map((program) => {
                const safeStatus = program.status || 'Active';
                const statusClass = safeStatus.toLowerCase().replace(/\s+/g, '-');
                
                return `
                  <tr>
                    <td>${program.title || 'N/A'}</td>
                    <td>${program.category || 'N/A'}</td>
                    <td>${program.student || 'N/A'}</td>
                    <td>${program.enrolledAt || 'N/A'}</td>
                    <td>${program.completionDate || 'Not set'}</td>
                    <td>${program.remainingDays !== null ? `${program.remainingDays} days` : 'N/A'}</td>
                    <td>${program.progress || 0}%</td>
                    <td class="status-${statusClass}">${safeStatus}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="page-footer" style="margin-top: auto;">
          ${getSignatureHtml('report')}
          ${getFooterHtml('report')}
        </div>
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-content {
      min-height: 1050px;
      position: relative;
      display: flex;
      flex-direction: column;
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    .table-container {
      flex: 1;
      margin-bottom: 20px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
    }
    th, td {
      padding: 6px 4px;
      border-bottom: 1px solid #ddd;
      text-align: left;
      font-size: 0.8em;
      vertical-align: top;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
      border-bottom: 2px solid #ccc;
    }
    .status-active {
      color: #2e7d32;
      font-weight: 500;
    }
    .status-completed {
      color: #1976d2;
      font-weight: 500;
    }
    .status-inactive {
      color: #d32f2f;
      font-weight: 500;
    }
    .status-pending {
      color: #ed6c02;
      font-weight: 500;
    }
    .page-footer {
      margin-top: auto;
      page-break-inside: avoid;
      position: relative;
      min-height: 120px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }
    .signatures {
      margin-top: 30px;
      margin-bottom: 15px;
      flex-shrink: 0;
    }
    .footer {
      margin-top: 15px;
      flex-shrink: 0;
      position: relative;
    }
    @page {
      size: A4;
      margin: 0.5in;
    }
    @media print {
      .page-content {
        page-break-after: always;
        min-height: 1050px;
      }
      .page-content:last-child {
        page-break-after: auto;
      }
    }
  `;

  // Generate the complete print document
  const printDocument = createPrintDocument(allPagesContent, 'Programs_List_Paginated', styles);
  
  // Create filename
  const filename = actualStartPage === actualEndPage 
    ? `Programs_List_Page_${actualStartPage}_${new Date().toISOString().split('T')[0]}`
    : `Programs_List_Pages_${actualStartPage}-${actualEndPage}_${new Date().toISOString().split('T')[0]}`;

  return { content: printDocument, filename };
};

/**
 * Export individual program as JPG
 */
export const printIndividualProgramAsJPG = async (program: ProgramData) => {
  try {
    console.log('Starting individual program JPG download...', program);
    
    const loadingToast = toast.loading(`Generating ${program.title || 'program'} details...`);
    
    const { content, filename } = generateIndividualProgramContent(program);
    
    await downloadAsJPG(content, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download program details');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success(`Program details for "${program.title || 'Program'}" downloaded as JPG!`);
    
  } catch (error) {
    console.error('Error downloading individual program:', error);
    toast.error('Failed to download program details');
  }
};

/**
 * Export individual program as PDF
 */
export const printIndividualProgramAsPDF = async (program: ProgramData) => {
  try {
    console.log('Starting individual program PDF download...', program);
    
    const loadingToast = toast.loading(`Generating ${program.title || 'program'} details...`);
    
    const { content, filename } = generateIndividualProgramContent(program);
    
    await downloadAsPDF(content, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download program details');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success(`Program details for "${program.title || 'Program'}" downloaded as PDF!`);
    
  } catch (error) {
    console.error('Error downloading individual program as PDF:', error);
    toast.error('Failed to download program details');
  }
};

/**
 * Generate individual program content for both JPG and PDF
 */
const generateIndividualProgramContent = (program: ProgramData) => {
  const safeStatus = program.status || 'Active';
  
  const content = `
    <div class="page-content" style="min-height: 1050px; display: flex; flex-direction: column;">
      ${getHeaderHtml(`Program Details - ${program.title || 'Program'}`)}
      
      <div class="program-details" style="flex: 1; margin: 20px 0;">
        <div class="detail-section">
          <h3>Program Information</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <strong>Program Title:</strong> ${program.title || 'N/A'}
            </div>
            <div class="detail-item">
              <strong>Category:</strong> ${program.category || 'N/A'}
            </div>
            <div class="detail-item">
              <strong>Student:</strong> ${program.student || 'N/A'}
            </div>
            <div class="detail-item">
              <strong>Enrolled Date:</strong> ${program.enrolledAt || 'N/A'}
            </div>
            <div class="detail-item">
              <strong>Completion Date:</strong> ${program.completionDate || 'Not set'}
            </div>
            <div class="detail-item">
              <strong>Remaining Days:</strong> ${program.remainingDays !== null ? `${program.remainingDays} days` : 'N/A'}
            </div>
            <div class="detail-item">
              <strong>Progress:</strong> ${program.progress || 0}%
            </div>
            <div class="detail-item">
              <strong>Status:</strong> ${safeStatus}
            </div>
          </div>
        </div>
      </div>
      
      <div class="page-footer" style="margin-top: auto;">
        ${getSignatureHtml('report')}
        ${getFooterHtml('report')}
      </div>
    </div>
  `;

  const styles = `
    ${getCommonPrintStyles()}
    .program-details {
      padding: 20px;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: #fafafa;
    }
    .detail-section h3 {
      margin-bottom: 15px;
      color: #3D7069;
      border-bottom: 2px solid #3D7069;
      padding-bottom: 5px;
    }
    .detail-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .detail-item {
      padding: 8px;
      background: white;
      border-radius: 4px;
      border: 1px solid #eee;
    }
    .detail-item strong {
      color: #333;
      display: block;
      margin-bottom: 4px;
    }
  `;

  const printDocument = createPrintDocument(content, 'Individual_Program', styles);
  const safeTitle = (program.title || 'Program').replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `Program_${safeTitle}_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};
